import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  ArrowRight,
  Eye,
  EyeOff,
  KeyRound,
  Loader2,
  Lock,
  Mail,
  Recycle,
  ShieldCheck,
} from 'lucide-react';
import { publicApi } from '../contexts/AuthContext';

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const inputClass = 'block w-full rounded-2xl border border-slate-300 bg-white pl-11 pr-4 py-3 text-slate-900 shadow-sm transition-all duration-200 placeholder:text-slate-400 focus:border-emerald-500 focus:outline-none focus:ring-4 focus:ring-emerald-100';
  const passwordInputClass = `${inputClass} pr-12`;
  const primaryButtonClass = 'w-full flex items-center justify-center gap-2 rounded-2xl bg-emerald-600 px-6 py-3.5 text-white font-semibold shadow-sm transition-all duration-200 hover:bg-emerald-700 hover:shadow-lg hover:shadow-emerald-200/60 disabled:cursor-not-allowed disabled:opacity-60 active:scale-[0.98]';
  const otpBoxClass = 'h-12 w-12 rounded-xl border border-slate-300 bg-white text-center text-2xl font-mono text-slate-900 shadow-sm transition-all duration-200 focus:border-emerald-500 focus:outline-none focus:ring-4 focus:ring-emerald-100';

  const handleOtpChange = (value, index) => {
    if (value.length > 1) value = value[0];
    if (!/^\d?$/.test(value)) return;

    const nextOtp = [...otp];
    nextOtp[index] = value;
    setOtp(nextOtp);

    if (value && index < 5) {
      document.getElementById(`reset-otp-${index + 1}`)?.focus();
    }
  };

  const handleOtpKeyDown = (event, index) => {
    if (event.key === 'Backspace' && !otp[index] && index > 0) {
      document.getElementById(`reset-otp-${index - 1}`)?.focus();
    }
  };

  const handleSendCode = async () => {
    if (!email.trim()) {
      setError('Email is required');
      toast.error('Email is required');
      return;
    }

    setIsLoading(true);
    setError('');
    setMessage('');

    try {
      const { data } = await publicApi.post('/api/auth/forgot-password', { email: email.trim() });
      setOtpSent(true);
      setOtp(['', '', '', '', '', '']);
      setMessage(data.message);
      toast.success('Reset code sent if the account exists.');
    } catch (err) {
      const nextError = err.response?.data?.message || 'Failed to send reset code';
      setError(nextError);
      toast.error(nextError);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (event) => {
    event.preventDefault();

    if (otp.join('').length !== 6) {
      setError('Enter the 6-digit reset code');
      toast.error('Enter the 6-digit reset code');
      return;
    }

    if (newPassword.trim().length < 6) {
      setError('New password must be at least 6 characters');
      toast.error('New password must be at least 6 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      toast.error('Passwords do not match');
      return;
    }

    setIsLoading(true);
    setError('');
    setMessage('');

    try {
      const { data } = await publicApi.post('/api/auth/reset-password', {
        email: email.trim(),
        otp: otp.join(''),
        newPassword: newPassword.trim(),
      });

      toast.success('Password reset successfully');
      setMessage(data.message);
      navigate('/login', { replace: true });
    } catch (err) {
      const nextError = err.response?.data?.message || 'Failed to reset password';
      setError(nextError);
      toast.error(nextError);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-slate-950 px-4 py-10 sm:px-6 lg:px-8">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(16,185,129,0.14),transparent_28%),radial-gradient(circle_at_bottom_right,rgba(20,184,166,0.12),transparent_24%)]" />
      <div className="relative mx-auto flex min-h-[calc(100vh-5rem)] max-w-5xl items-center">
        <div className="grid w-full items-start overflow-hidden rounded-[28px] border border-white/10 bg-white shadow-[0_30px_80px_rgba(15,23,42,0.45)] lg:grid-cols-[0.92fr_1.08fr]">
          <aside className="relative hidden lg:flex h-full flex-col justify-start gap-10 overflow-hidden bg-slate-900 px-8 py-9 text-white xl:gap-12">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(16,185,129,0.22),transparent_30%),radial-gradient(circle_at_bottom_right,rgba(20,184,166,0.18),transparent_28%)]" />
            <div className="relative z-10">
              <div className="inline-flex items-center gap-3 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm text-slate-200 backdrop-blur">
                <Recycle className="h-4 w-4 text-emerald-400" />
                Account Recovery
              </div>
              <h1 className="mt-8 text-3xl font-bold leading-tight tracking-tight">
                Reset your password securely.
              </h1>
              <p className="mt-4 max-w-md text-sm leading-7 text-slate-300">
                We will send a 6-digit code to your email. Use it to create a new password and get back into your account.
              </p>
            </div>

            <div className="relative z-10 space-y-4">
              {[
                'Request a reset code using your registered email address.',
                'Enter the OTP and choose a new password securely.',
                'Return to login and continue using your dashboard normally.',
              ].map((item) => (
                <div key={item} className="rounded-2xl border border-white/10 bg-white/5 p-4 backdrop-blur-sm">
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-300">
                      <ShieldCheck className="h-4 w-4" />
                    </div>
                    <p className="text-sm leading-6 text-slate-200">{item}</p>
                  </div>
                </div>
              ))}
            </div>
          </aside>

          <div className="flex flex-col justify-start px-6 py-8 sm:px-8 sm:py-10 lg:px-10">
            <div className="mb-8 flex items-start justify-between gap-4">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full border border-emerald-100 bg-emerald-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-emerald-700">
                  Forgot Password
                </div>
                <h2 className="mt-4 text-3xl font-bold tracking-tight text-slate-900">Reset access</h2>
                <p className="mt-2 text-sm leading-6 text-slate-600">
                  Use your email to receive a secure reset code and create a new password.
                </p>
              </div>
              <div className="hidden h-12 w-12 items-center justify-center rounded-2xl bg-slate-100 text-slate-700 sm:flex">
                <KeyRound className="h-6 w-6" />
              </div>
            </div>

            {error && (
              <div className="mb-6 flex items-center gap-3 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-red-700">
                <ShieldCheck className="h-5 w-5 shrink-0" />
                <p className="text-sm">{error}</p>
              </div>
            )}

            {message && (
              <div className="mb-6 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
                {message}
              </div>
            )}

            <form className="space-y-6" onSubmit={handleResetPassword}>
              <div>
                <label htmlFor="reset-email" className="mb-1.5 block text-sm font-medium text-slate-700">
                  Email address
                </label>
                <div className="relative">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5">
                    <Mail className="h-5 w-5 text-slate-400" />
                  </div>
                  <input
                    id="reset-email"
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    className={inputClass}
                    placeholder="you@example.com"
                    disabled={isLoading || otpSent}
                    required
                  />
                </div>
              </div>

              {!otpSent ? (
                <button
                  type="button"
                  onClick={handleSendCode}
                  disabled={isLoading || !email.trim()}
                  className={primaryButtonClass}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      Sending code...
                    </>
                  ) : (
                    <>
                      Send reset code
                      <ArrowRight className="h-5 w-5" />
                    </>
                  )}
                </button>
              ) : (
                <>
                  <div>
                    <label className="mb-1.5 flex items-center gap-2 text-sm font-medium text-slate-700">
                      <ShieldCheck className="h-5 w-5 text-emerald-600" />
                      Enter 6-digit code
                    </label>
                    <div className="flex justify-center gap-3">
                      {otp.map((digit, index) => (
                        <input
                          key={index}
                          id={`reset-otp-${index}`}
                          type="text"
                          maxLength={1}
                          value={digit}
                          onChange={(event) => handleOtpChange(event.target.value, index)}
                          onKeyDown={(event) => handleOtpKeyDown(event, index)}
                          className={otpBoxClass}
                          autoComplete="one-time-code"
                          disabled={isLoading}
                        />
                      ))}
                    </div>
                  </div>

                  <div>
                    <label htmlFor="new-password" className="mb-1.5 block text-sm font-medium text-slate-700">
                      New password
                    </label>
                    <div className="relative">
                      <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5">
                        <Lock className="h-5 w-5 text-slate-400" />
                      </div>
                      <input
                        id="new-password"
                        type={showPassword ? 'text' : 'password'}
                        value={newPassword}
                        onChange={(event) => setNewPassword(event.target.value)}
                        className={passwordInputClass}
                        placeholder="Create a new password"
                        disabled={isLoading}
                        minLength={6}
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((prev) => !prev)}
                        className="absolute inset-y-0 right-0 flex items-center pr-3.5 text-slate-400 hover:text-slate-600"
                      >
                        {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="confirm-password" className="mb-1.5 block text-sm font-medium text-slate-700">
                      Confirm password
                    </label>
                    <div className="relative">
                      <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5">
                        <Lock className="h-5 w-5 text-slate-400" />
                      </div>
                      <input
                        id="confirm-password"
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={confirmPassword}
                        onChange={(event) => setConfirmPassword(event.target.value)}
                        className={passwordInputClass}
                        placeholder="Re-enter your new password"
                        disabled={isLoading}
                        minLength={6}
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword((prev) => !prev)}
                        className="absolute inset-y-0 right-0 flex items-center pr-3.5 text-slate-400 hover:text-slate-600"
                      >
                        {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                      </button>
                    </div>
                  </div>

                  <button type="submit" disabled={isLoading} className={primaryButtonClass}>
                    {isLoading ? (
                      <>
                        <Loader2 className="h-5 w-5 animate-spin" />
                        Resetting password...
                      </>
                    ) : (
                      <>
                        Save new password
                        <ArrowRight className="h-5 w-5" />
                      </>
                    )}
                  </button>

                  <div className="text-center">
                    <button
                      type="button"
                      onClick={() => {
                        setOtpSent(false);
                        setOtp(['', '', '', '', '', '']);
                        setMessage('');
                        setError('');
                      }}
                      className="text-sm font-medium text-emerald-700 transition-colors hover:text-emerald-800 hover:underline"
                      disabled={isLoading}
                    >
                      Didn't receive the code? Send again
                    </button>
                  </div>
                </>
              )}
            </form>

            <div className="mt-8 space-y-3 text-center">
              <p className="text-sm text-slate-600">
                Remembered your password?{' '}
                <Link to="/login" className="font-semibold text-emerald-700 transition-colors hover:text-emerald-800 hover:underline">
                  Back to sign in
                </Link>
              </p>
              <p className="text-sm text-slate-500">
                Need a new account?{' '}
                <Link to="/register" className="font-semibold text-emerald-700 transition-colors hover:text-emerald-800 hover:underline">
                  Create one
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
